import { useState, useEffect, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, CalendarCheck, Sparkles, TrendingUp, TrendingDown, Minus, MapPin, Calendar, Users, Link2, Trash2, Copy, Download } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'

interface PressRow {
  id: string
  outlet: string
  journalist: string
  contactEmail: string
  type: string
  market?: string
  proposedAt?: string
  notes?: string
  status: string
  createdAt: string
}

interface PressBrief {
  generatedAt: string
  career: Array<{ date: string; stage: string; stageScore: number | null; momentum: string; momentumScore: number | null }>
  venues: Array<{ venueId: number; name: string; city: string | null; countryCode: string | null; capacity: number | null; events: Array<{ eventName: string }> }>
  festivals: Array<{ festivalId: number; name: string; date: string; city: string | null; country: string | null; eventSize: string | null; popularityRank: number | null }>
  relatedArtists: Array<{ cmArtistId: number; name: string; imageUrl: string | null; cmArtistScore: number | null }>
}

export function PressPage() {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const { data, error, isLoading } = useQuery<PressRow[], ApiClientError>({
    queryKey: ['press'],
    queryFn: () => artistFetch<PressRow[]>('/press'),
  })

  async function schedule(id: string) {
    await artistFetch(`/press/${id}/schedule`, { method: 'POST' })
    qc.invalidateQueries({ queryKey: ['press'] })
  }

  return (
    <div>
      <PageHeader
        title="Press & Media"
        subtitle="Interview requests, coverage tracking, and media management."
        actions={
          <button
            type="button"
            onClick={() => setAdding((a) => !a)}
            className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
          >
            <Plus className="h-4 w-4" />
            {adding ? 'Cancel' : 'Add request'}
          </button>
        }
      />
      {adding && (
        <AddPressForm
          onCreated={() => {
            setAdding(false)
            qc.invalidateQueries({ queryKey: ['press'] })
          }}
        />
      )}
      <PressBriefPanel />
      <EpkLinksPanel />
      <AsyncBoundary isLoading={isLoading} error={error}>
        <DataTable
          rows={data ?? []}
          rowKey={(r) => r.id}
          emptyMessage="No press requests yet."
          columns={[
            { key: 'outlet', header: 'Outlet', render: (r) => r.outlet },
            { key: 'journalist', header: 'Journalist', render: (r) => r.journalist },
            {
              key: 'type',
              header: 'Type',
              render: (r) => <span className="capitalize">{r.type}</span>,
            },
            { key: 'market', header: 'Market', render: (r) => r.market ?? '—' },
            {
              key: 'status',
              header: 'Status',
              render: (r) => (
                <span className="capitalize">{r.status.replace(/_/g, ' ')}</span>
              ),
            },
            {
              key: 'actions',
              header: '',
              render: (r) =>
                r.status === 'qualified' ? (
                  <button
                    type="button"
                    onClick={() => schedule(r.id)}
                    className="flex items-center gap-1 text-sm text-blue-700 hover:underline"
                  >
                    <CalendarCheck className="h-3 w-3" />
                    Schedule
                  </button>
                ) : null,
            },
          ]}
        />
      </AsyncBoundary>
    </div>
  )
}

function AddPressForm({ onCreated }: { onCreated: () => void }) {
  const [outlet, setOutlet] = useState('')
  const [journalist, setJournalist] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [type, setType] = useState('interview')
  const [market, setMarket] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      await artistFetch('/press', {
        method: 'POST',
        body: {
          outlet,
          journalist,
          contactEmail,
          type,
          market: market || undefined,
          notes: notes || undefined,
        },
      })
      onCreated()
    } catch (err) {
      setErr(err instanceof ApiClientError ? err.message : 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="mb-6 space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Outlet</label>
          <input required value={outlet} onChange={(e) => setOutlet(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="Rolling Stone" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Journalist</label>
          <input required value={journalist} onChange={(e) => setJournalist(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Contact email</label>
          <input type="email" required value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Type</label>
          <select value={type} onChange={(e) => setType(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm">
            <option value="interview">Interview</option>
            <option value="feature">Feature</option>
            <option value="review">Review</option>
            <option value="photo">Photo</option>
            <option value="broadcast">Broadcast</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Market</label>
          <input value={market} onChange={(e) => setMarket(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="UK, US, etc." />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Notes</label>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <button type="submit" disabled={submitting} className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50">
        {submitting ? 'Creating...' : 'Create request'}
      </button>
    </form>
  )
}

function PressBriefPanel() {
  const [brief, setBrief] = useState<PressBrief | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setErr] = useState<string | null>(null)

  // Load the cached brief on mount — no API credits charged for the read.
  useEffect(() => {
    let cancelled = false
    artistFetch<PressBrief | null>('/press/intel')
      .then((data) => {
        if (!cancelled && data) setBrief(data)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  async function generate() {
    const promptText = brief
      ? 'Refresh PR Brief from Chartmetric? This will overwrite the cached brief and cost ~£0.25 in API credits.'
      : 'Generate PR Brief from Chartmetric? This will fetch career, venues, festivals, and related artists (~£0.25 in API credits).'
    if (!confirm(promptText)) return
    setLoading(true)
    setErr(null)
    try {
      const data = await artistFetch<PressBrief>('/press/intel/refresh', { method: 'POST' })
      setBrief(data)
    } catch (err) {
      setErr(err instanceof ApiClientError ? err.message : 'Failed to generate brief')
    } finally {
      setLoading(false)
    }
  }

  const latestCareer = brief?.career[brief.career.length - 1] ?? null
  const momentumIcon = latestCareer?.momentum === 'up'
    ? <TrendingUp className="h-4 w-4 text-emerald-600" />
    : latestCareer?.momentum === 'down'
      ? <TrendingDown className="h-4 w-4 text-red-600" />
      : <Minus className="h-4 w-4 text-zinc-400" />

  return (
    <section className="mb-6 rounded-lg border border-ovation-ink/10 bg-white p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-ovation-ink" />
          <h2 className="text-base font-semibold text-ovation-ink">PR Talking Points</h2>
          {brief && (
            <span className="text-xs text-ovation-muted">
              Generated {new Date(brief.generatedAt).toLocaleString()}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={loading}
          className="rounded-md border border-ovation-ink/15 bg-white px-3 py-1.5 text-xs font-medium text-ovation-ink hover:bg-ovation-paper disabled:opacity-50"
        >
          {loading ? 'Generating…' : brief ? 'Refresh (~£0.25)' : 'Generate brief (~£0.25)'}
        </button>
      </div>

      {error && <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      {!brief && !error && (
        <p className="text-sm text-ovation-muted">
          Auto-generated talking points pulled from Chartmetric — career stage, recent venues, upcoming festivals, and related artists. Useful as a starting point for journalist pitches and PR briefs.
        </p>
      )}

      {brief && (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {/* Career */}
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-xs uppercase tracking-wider text-ovation-muted">
              <TrendingUp className="h-3 w-3" /> Career snapshot
            </div>
            {latestCareer ? (
              <div>
                <div className="flex items-baseline gap-2">
                  <span className="text-lg font-semibold capitalize text-ovation-ink">{latestCareer.stage}</span>
                  {latestCareer.stageScore != null && (
                    <span className="text-xs text-ovation-muted">score {latestCareer.stageScore}/100</span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-1.5 text-sm text-ovation-muted">
                  {momentumIcon}
                  <span className="capitalize">{latestCareer.momentum} momentum</span>
                  {latestCareer.momentumScore != null && (
                    <span className="text-xs">({latestCareer.momentumScore})</span>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-ovation-muted">No career data available.</p>
            )}
          </div>

          {/* Related artists */}
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-xs uppercase tracking-wider text-ovation-muted">
              <Users className="h-3 w-3" /> Comparison artists
            </div>
            {brief.relatedArtists.length > 0 ? (
              <ul className="space-y-1 text-sm text-ovation-ink">
                {brief.relatedArtists.slice(0, 5).map((a) => (
                  <li key={a.cmArtistId} className="flex items-center justify-between">
                    <span>{a.name}</span>
                    {a.cmArtistScore != null && (
                      <span className="text-xs text-ovation-muted">{Math.round(a.cmArtistScore)}/100</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-ovation-muted">No related artists found.</p>
            )}
          </div>

          {/* Recent venues */}
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-xs uppercase tracking-wider text-ovation-muted">
              <MapPin className="h-3 w-3" /> Recent venues
            </div>
            {brief.venues.length > 0 ? (
              <ul className="space-y-1 text-sm text-ovation-ink">
                {brief.venues.slice(0, 5).map((v) => (
                  <li key={v.venueId}>
                    <span className="font-medium">{v.name}</span>
                    {(v.city || v.countryCode) && (
                      <span className="text-xs text-ovation-muted"> · {[v.city, v.countryCode].filter(Boolean).join(', ')}</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-ovation-muted">No venue history available.</p>
            )}
          </div>

          {/* Upcoming festivals */}
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-xs uppercase tracking-wider text-ovation-muted">
              <Calendar className="h-3 w-3" /> Upcoming festivals
            </div>
            {brief.festivals.length > 0 ? (
              <ul className="space-y-1 text-sm text-ovation-ink">
                {brief.festivals.slice(0, 5).map((f) => (
                  <li key={f.festivalId}>
                    <span className="font-medium">{f.name}</span>
                    <span className="text-xs text-ovation-muted">
                      {' · '}{f.date?.slice(0, 10)}
                      {f.country && ` · ${f.country}`}
                      {f.eventSize && ` · ${f.eventSize}`}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-ovation-muted">No upcoming festivals.</p>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

// ── EPK Links panel ──────────────────────────────────────────────────────────

interface EpkLinkRow {
  id: string
  variant: string
  token: string
  label: string | null
  views: number
  lastViewedAt: string | null
  expiresAt: string | null
  createdAt: string
  eventCount?: number
  docxDownloads?: number
}

const VARIANT_LABELS: Record<string, string> = {
  concert: 'Concert',
  festival: 'Festival',
  corporate: 'Corporate',
  private_party: 'Private event',
  charity_gala: 'Charity / Gala',
  commercial_tv_film: 'TV / Film',
  general: 'General press kit',
}

function EpkLinksPanel() {
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [variant, setVariant] = useState('concert')
  const [label, setLabel] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const { data, isLoading } = useQuery<EpkLinkRow[], ApiClientError>({
    queryKey: ['epk-links'],
    queryFn: () => artistFetch<EpkLinkRow[]>('/press/epk-links'),
  })

  async function create() {
    setSubmitting(true)
    setErr(null)
    try {
      await artistFetch('/press/epk-links', {
        method: 'POST',
        body: { variant, label: label || undefined },
      })
      setCreating(false)
      setLabel('')
      qc.invalidateQueries({ queryKey: ['epk-links'] })
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : 'Failed to create link')
    } finally {
      setSubmitting(false)
    }
  }

  async function revoke(id: string) {
    if (!confirm('Revoke this EPK link? Anyone with the URL will lose access.')) return
    try {
      await artistFetch(`/press/epk-links/${id}`, { method: 'DELETE' })
      qc.invalidateQueries({ queryKey: ['epk-links'] })
    } catch {
      /* swallow */
    }
  }

  function copyUrl(token: string) {
    const slug = window.location.hostname.includes('localhost') ? 'smokey-robinson' : 'smokey-robinson'
    const url = `${window.location.origin}/epk/${slug}/${token}`
    navigator.clipboard.writeText(url)
  }

  function downloadUrl(token: string): string {
    const apiBase = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '')
    const slug = 'smokey-robinson'
    return `${apiBase}/public/epk/${slug}/${token}/download.docx`
  }

  return (
    <section className="mb-6 rounded-lg border border-ovation-ink/10 bg-white p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link2 className="h-5 w-5 text-ovation-ink" />
          <h2 className="text-base font-semibold text-ovation-ink">EPK links</h2>
          <span className="text-xs text-ovation-muted">Tokenized public EPKs by event type</span>
        </div>
        <button
          type="button"
          onClick={() => setCreating((c) => !c)}
          className="flex items-center gap-1.5 rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90"
        >
          <Plus className="h-3 w-3" />
          {creating ? 'Cancel' : 'New link'}
        </button>
      </div>

      {creating && (
        <div className="mb-4 grid grid-cols-1 gap-3 rounded-md bg-ovation-paper/40 p-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Variant</label>
            <select
              value={variant}
              onChange={(e) => setVariant(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
            >
              {Object.entries(VARIANT_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Label (optional)</label>
            <div className="flex gap-2">
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. AEG Touring UK summer 2026"
                className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={create}
                disabled={submitting}
                className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
              >
                {submitting ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
          {err && <div className="text-sm text-red-700 sm:col-span-3">{err}</div>}
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-ovation-muted">Loading…</p>
      ) : (data ?? []).length === 0 ? (
        <p className="text-sm text-ovation-muted">No EPK links yet. Inbound bookings auto-create one per submission; you can also mint targeted links above.</p>
      ) : (
        <ul className="divide-y divide-ovation-ink/5">
          {(data ?? []).map((link) => (
            <li key={link.id} className="flex flex-wrap items-center justify-between gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="rounded bg-ovation-paper/60 px-2 py-0.5 text-xs font-medium text-ovation-ink">
                    {VARIANT_LABELS[link.variant] ?? link.variant}
                  </span>
                  <span className="truncate text-sm text-ovation-ink">{link.label ?? '(no label)'}</span>
                </div>
                <div className="mt-0.5 text-xs text-ovation-muted">
                  {link.views} view{link.views === 1 ? '' : 's'}
                  {typeof link.docxDownloads === 'number' &&
                    ` · ${link.docxDownloads} docx`}
                  {link.lastViewedAt && ` • last viewed ${new Date(link.lastViewedAt).toLocaleDateString()}`}
                  {' • '}created {new Date(link.createdAt).toLocaleDateString()}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => copyUrl(link.token)}
                  title="Copy public URL"
                  className="rounded-md border border-ovation-ink/15 bg-white px-2 py-1 text-xs font-medium text-ovation-ink hover:bg-ovation-paper"
                >
                  <Copy className="h-3 w-3" />
                </button>
                <a
                  href={downloadUrl(link.token)}
                  target="_blank"
                  rel="noreferrer"
                  title="Download .docx"
                  className="rounded-md border border-ovation-ink/15 bg-white px-2 py-1 text-xs font-medium text-ovation-ink hover:bg-ovation-paper"
                >
                  <Download className="h-3 w-3" />
                </a>
                <button
                  type="button"
                  onClick={() => revoke(link.id)}
                  title="Revoke"
                  className="rounded-md border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
